/**
 * CadenceBackfillPanel.jsx — one-off + repeatable tool: upload the BD
 * campaign-tracking export (aggregate per-send stats, not per-recipient
 * detail — columns like Date Sent/Recipients/Subject/Email Open Rate/
 * List Names) and match each row against existing bd_uploads by date +
 * subject similarity to backfill list_name. Rows that don't match anything
 * are shown so you know which campaigns still need a real detail-file
 * upload through the normal flow above before they can be tagged.
 */
import { useState } from 'react';
import { parseBigDoughFile, backfillListNames } from '../api/bigdough.js';

function findValue(row, keys) {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') return row[k];
  }
  return '';
}

export default function CadenceBackfillPanel({ getToken }) {
  const [expanded, setExpanded] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | parsing | running | done | error
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  async function handleFile(file) {
    if (!file) return;
    setPhase('parsing');
    setError('');
    setResult(null);
    try {
      const { rows } = await parseBigDoughFile(file);
      const mapped = rows
        .map((r) => {
          const openRateRaw = findValue(r, ['Email Open Rate', 'email open rate', 'Open Rate']);
          const openRate = openRateRaw ? parseFloat(String(openRateRaw).replace('%', '')) / 100 : null;
          return {
            sent_at:    findValue(r, ['Date Sent', 'date sent', 'Sent']),
            recipients: Number(findValue(r, ['Recipients', 'recipients'])) || 0,
            subject:    findValue(r, ['Subject', 'subject']),
            list_names: findValue(r, ['List Names', 'list names', 'List Name']),
            opens:      Number(findValue(r, ['Email Opened', 'email opened'])) || 0,
            open_rate:  Number.isFinite(openRate) ? openRate : null,
          };
        })
        .filter((r) => r.sent_at && r.subject);

      if (!mapped.length) {
        setError('No usable rows found — expected columns like "Date Sent", "Subject", "Recipients", "List Names".');
        setPhase('error');
        return;
      }

      setPhase('running');
      const res = await backfillListNames(getToken, mapped);
      setResult(res);
      setPhase('done');
    } catch (e) {
      setError(e.message || 'Backfill failed');
      setPhase('error');
    }
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--navy)' }}
      >
        <span>{expanded ? '▾' : '▸'}</span>
        Backfill list names from a tracking export
      </div>

      {expanded && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8 }}>
            Upload a campaign-tracking CSV (Date Sent, Recipients, Subject, Email Open Rate, List Names —
            aggregate stats, not per-recipient detail). Matches each row to an existing upload by date + subject
            and fills in list_name — never overwrites one that's already set.
          </div>

          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={phase === 'parsing' || phase === 'running'}
            style={{ fontSize: 12 }}
          />

          {phase === 'parsing' && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>Parsing file…</div>}
          {phase === 'running' && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>Matching against existing uploads…</div>}
          {phase === 'error' && <div style={{ fontSize: 12, color: 'var(--red, #dc2626)', marginTop: 8 }}>⚠ {error}</div>}

          {phase === 'done' && result && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12.5, color: 'var(--green, #059669)', marginBottom: 8 }}>
                ✓ Matched {result.matched.length} — filled in list_name on the upload and its outreach log rows.
              </div>
              {result.matched.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 10 }}>
                  {result.matched.map((m) => (
                    <div key={m.upload_id}>{m.list_name} → "{(m.subject || '').slice(0, 60)}"</div>
                  ))}
                </div>
              )}
              {result.unmatched.length > 0 && (
                <>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--amber, #d97706)', marginBottom: 6 }}>
                    {result.unmatched.length} row(s) had no matching upload — these need the real detail file
                    uploaded through "Drop BigDough export here" above before they can be tagged:
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', maxHeight: 200, overflowY: 'auto' }}>
                    {result.unmatched.map((u, i) => (
                      <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
                        {u.sent_at} · {u.recipients} recipients · {u.list_names} · "{(u.subject || '').slice(0, 60)}"
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
